'use client';

import { useTranslations } from 'next-intl';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { cn } from '@/app/utils';
import type { TypesVideoResource } from './types';

interface TemplateCardProps {
  /** landscape:横屏展示，portrait:竖屏展示 */
  variant: 'landscape' | 'portrait';
  resource?: TypesVideoResource;
  title: string;
  /** 如果为 true，则右上角展示图片类型图标 */
  isImageType?: boolean;
  imgClassName?: string;
  hoverClassName?: string;
  className?: string;
  // hot/new标签配置链接
  tag?: string;
  showCreate?: boolean;
  fromH5?: boolean;
  titleClassName?: string;
  onClick?: () => void;
  onViewDetail?: () => void;
  size?: 'small' | 'default';
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  tag,
  title,
  variant,
  resource,
  isImageType = false,
  imgClassName,
  hoverClassName,
  className,
  titleClassName,
  showCreate = false,
  onClick,
  onViewDetail,
  size = 'default',
}) => {
  const t = useTranslations('templates');

  return (
    <div onClick={onViewDetail || onClick}>
      <div
        className={cn(
          'group relative cursor-pointer overflow-hidden rounded-12',
          variant === 'landscape' ? 'aspect-video' : 'aspect-[3/4]',
          className,
        )}
      >
        <div className="absolute inset-0 h-full w-full overflow-hidden">
          <img
            src={resource?.cover_uri}
            alt=""
            className="h-full w-full scale-x-125 scale-y-105 object-cover blur-[20px]"
          />
        </div>
        <img
          src={resource?.uris?.low}
          alt={title}
          className={cn('relative h-full w-full cursor-pointer object-contain', imgClassName)}
        />
        <div className="absolute top-2 left-2 z-10 flex max-w-[calc(100%-16px)] items-center gap-1">
          <span
            className={cn(
              'flex-1 truncate font-medium text-sm text-white sm:text-xs',
              showCreate && 'hidden',
              titleClassName,
            )}
          >
            {title}
          </span>
          {tag && (
            <img src={tag} alt="" className="h-[22px] flex-shrink-0 rounded-6 object-contain backdrop-blur-[5px]" />
          )}
        </div>
        {isImageType && (
          <div className={cn('absolute top-2 right-2 z-10')}>
            <ProductLinePic className="text-white text-xl" />
          </div>
        )}
        <div
          className={cn(
            'absolute inset-0 z-10 flex flex-col items-center justify-center transition-all duration-200 ease-in-out',
            showCreate ? 'invisible no-hover:hidden group-hover:visible group-hover:bg-system-black48' : 'hidden',
            size === 'small' ? 'pt-6' : 'pt-8',
            hoverClassName,
          )}
        >
          <div
            className={cn(
              'flex cursor-pointer items-center justify-center justify-self-center bg-system-white16 font-semibold text-system-white backdrop-blur-[10px] hover:bg-system-white24',
              size === 'small' ? 'h-9 w-[90px] rounded-8 text-xs' : 'h-14 w-[124px] rounded-12',
            )}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            {t('button')}
          </div>
          {onViewDetail && (
            <p
              className={cn(size === 'small' ? 'mt-2 text-xs' : 'mt-3 text-sm')}
              onClick={(e) => {
                e.stopPropagation();
                onViewDetail();
              }}
            >
              {t('viewDetails')}
            </p>
          )}
        </div>
      </div>
      <div className={cn('mt-2 ml-2 truncate font-normal text-sm sm:text-xs', !showCreate && 'hidden', titleClassName)}>
        {title}
      </div>
    </div>
  );
};
